import { fetchFxRate } from "../api";
import { AdvanceAmount, ExpenseLineItem, FxOverride } from "../types";

/** The compound key every rate/override map in this module is keyed by -
 *  exported so callers (CurrencyConversionCard.tsx) build pending-override
 *  and clearing-state keys the exact same way, rather than duplicating the
 *  "currency->target" string format. */
export function pairKey(currency: string, targetCurrency: string): string {
  return `${currency}->${targetCurrency}`;
}

/** Truncates an ISO datetime (e.g. departureAt - "2026-10-15T09:00:00Z" or
 *  "2026-10-15T09:00" from a datetime-local input) down to its plain
 *  YYYY-MM-DD date, which is the only format Frankfurter's own ?date= param
 *  accepts (confirmed: fx.py passes the date straight through with no
 *  parsing of its own). Passing a full datetime through unmodified silently
 *  degrades to "no rate for this date" for EVERY advance line, not a loud
 *  error - every bill's own ExpenseLineItem.date is already plain
 *  YYYY-MM-DD, so only departureDate (the advance's conversion date - see
 *  fxLedgerKeys) ever needs this. */
function toFxDate(isoOrNull: string | null | undefined): string | undefined {
  return isoOrNull ? isoOrNull.slice(0, 10) : undefined;
}

/** True when the bills and linked advance combined touch more than one
 *  currency - the one condition that decides whether CurrencyConversionCard
 *  renders a ledger at all (it renders nothing otherwise, there being
 *  nothing to reconcile). Exported so a caller showing its own, simpler
 *  per-currency net-payable figure (RequestDetailDialog's "Linked advance"
 *  card) can hide it exactly when the ledger below it would show the same
 *  thing instead, without duplicating this currency-set logic. */
export function spansMultipleCurrencies(
  expenses: ExpenseLineItem[],
  advanceAmounts: AdvanceAmount[],
  fallbackCurrency: string
): boolean {
  const currencies = new Set([
    ...expenses.map((e) => e.currency || fallbackCurrency),
    ...advanceAmounts.map((a) => a.currency)
  ]);
  return currencies.size > 1;
}

/** One (date, rate) actually used to convert part of a row's position -
 *  either one bill-date's worth of bills, or the advance's own conversion
 *  (dated at the trip's departure, labelled "Advance" since it isn't a bill
 *  date). Exposed so the UI can show the real breakdown instead of
 *  collapsing several genuinely different rates into one misleading
 *  number - see FxLedgerRow.rate's own doc comment. */
export interface FxRateUsed {
  /** The bill date this rate was fetched for, or null for the advance's
   *  own line (labelled "Advance" in the UI, not a date). */
  date: string | null;
  rate: number;
  /** This date/line's own portion of the row's amount, in `currency` -
   *  positive for a bill-date group, negative for the advance (it offsets
   *  the bills) - so breakdown entries sum to sameCurrOffset. */
  amount: number;
}

/** One currency's reconciliation row - nets the advance issued in that
 *  currency against the bills actually spent in it, THEN converts only the
 *  leftover ("residual position") into that row's OWN target currency.
 *  Netting within a currency first (rather than converting the raw advance
 *  and raw expense totals separately) means a currency with no advance, or
 *  no bills, still produces exactly one clean figure to convert. Each row
 *  can target a different settlement currency (a global picker just sets
 *  every row's starting point - see CurrencyConversionCard), so the ledger's
 *  net payment is grouped by distinct target currency, not one single
 *  number (see FxLedger.netPayments).
 *
 *  UNLESS overridden, each bill converts at ITS OWN transaction date's rate
 *  (not one representative date shared by every bill in the currency) and
 *  the advance converts at the trip's departure date (it has no date of
 *  its own - see fxLedgerKeys) - rateBreakdown lists every (date, rate)
 *  actually used, and valuationInTarget is their sum. An Override FX
 *  collapses all of that back to ONE flat rate for the whole row (same as
 *  before per-bill dating existed) - rate/asOfDate are only ever singular
 *  (an override, or a row with just one real rate behind it); once more
 *  than one distinct rate actually went into the row with no override set,
 *  rate is null and multipleRates is true instead of picking one to show
 *  as if it were the only one. */
export interface FxLedgerRow {
  currency: string;
  /** This row's OWN settlement currency - defaults to the ledger's global
   *  target but independently changeable per row. */
  targetCurrency: string;
  advanceIssued: number;
  actualExpense: number;
  /** actualExpense - advanceIssued: positive = owed TO the employee in this
   *  currency (they spent MORE than they were advanced, so the advance
   *  under-covered it), negative = owed BY the employee (the advance
   *  over-covered their spend). Same sign convention as netPayableByCurrency
   *  in lib/advance.ts (`basis - advancedHere`), so "payable"/positive here
   *  means the same thing it does everywhere else this app shows net
   *  payable/owed. Zero is omitted from the ledger entirely (see
   *  buildFxLedger) - nothing to reconcile. */
  sameCurrOffset: number;
  /** The row's one flat rate - set only for a same-currency row (always
   *  1), an override (saved or pending), or a row where every bill/the
   *  advance happened to need exactly one distinct rate (the common case:
   *  one bill, or several bills sharing one date). null when NEITHER of
   *  those applies - either multipleRates (several real rates went into
   *  this row, see rateBreakdown) or no FX coverage at all for this
   *  currency/target pair. */
  rate: number | null;
  /** True when rate is null because the row genuinely used more than one
   *  distinct rate (not because of missing coverage) - see rateBreakdown
   *  for the actual dates/rates/amounts. Mutually exclusive with rate being
   *  non-null; when both rate and this are null/false, it's a genuine "no
   *  FX coverage" row (counted in unconvertedOffsets instead). */
  multipleRates: boolean;
  /** Every (date, rate, amount) actually used to build valuationInTarget -
   *  populated whenever there's something to show (one entry for the
   *  common single-rate case too), empty only for a same-currency row, an
   *  override, or no-coverage-at-all. */
  rateBreakdown: FxRateUsed[];
  /** Set once a saved override exists for this row's (currency,
   *  targetCurrency) pair - the full record (reason, receipt, who/when),
   *  not just the rate, so the UI can show "why" alongside "what." Takes
   *  priority over a session-local (not-yet-saved) override, which in turn
   *  takes priority over the fetched Frankfurter rate(s). */
  savedOverride: FxOverride | null;
  /** True when a session-local (unsaved) override rate is in effect for
   *  this row - distinct from savedOverride, which is the persisted one. */
  isPendingOverride: boolean;
  /** The single date rate was fetched for - set only alongside a non-null
   *  rate from exactly one real (non-override) rate; null for a
   *  same-currency row, any override, a no-coverage row, or multipleRates
   *  (see rateBreakdown for each date in that case). */
  asOfDate: string | null;
  /** sameCurrOffset converted into targetCurrency - the sum of
   *  rateBreakdown's own amount*rate when there's no override, or
   *  sameCurrOffset*rate when there is one. null when there's no rate at
   *  all for this currency/target pair (nothing to convert this row
   *  into). */
  valuationInTarget: number | null;
  /** actualExpense (the bills only, NOT netted against the advance)
   *  converted into targetCurrency, at the same rate(s)/override as
   *  valuationInTarget. Not currently read by CurrencyConversionCard (both
   *  claim methods now show valuationInTarget - the advance-netted figure -
   *  in the Valuation column; see offsetMeaning's own doc comment there for
   *  why Fixed Allowance no longer gets a separate "plain spend, not netted"
   *  framing), kept for parity with FxLedger.spentByTarget, which still sums
   *  this field. null under the same "no rate available" condition as
   *  valuationInTarget. */
  actualExpenseInTarget: number | null;
}

/** One target currency's combined net payment, once every row pointed at
 *  it is summed. Positive = net payable to the employee in that currency,
 *  negative = net owed by them. */
export interface FxNetPayment {
  currency: string;
  amount: number;
}

export interface FxLedger {
  /** The ledger's starting/default target currency (the global picker) -
   *  individual rows may point elsewhere (see FxLedgerRow.targetCurrency). */
  defaultTargetCurrency: string;
  rows: FxLedgerRow[];
  /** One entry per distinct target currency actually used across the rows -
   *  a single-currency result (every row targets the same currency, the
   *  common case) is still a one-item array, so callers always read this
   *  the same way rather than special-casing "one target" vs "several." */
  netPayments: FxNetPayment[];
  /** sameCurrOffset left out of its row's target currency's net payment,
   *  per target currency, because that (currency, target) pair has no FX
   *  coverage at all - always shown separately, never silently dropped. */
  unconvertedOffsets: FxNetPayment[];
  /** Each row's actualExpenseInTarget summed per target currency - the
   *  Fixed Allowance counterpart to netPayments (which nets against the
   *  advance; this doesn't). Rows with no rate for their pair are excluded
   *  here the same way they're excluded from netPayments (and surfaced via
   *  unconvertedOffsets instead). */
  spentByTarget: FxNetPayment[];
}

type RateInfo = { rate: number | null; asOfDate: string | null; usedFallbackRate: boolean };

/** The compound key fetchLedgerFxRates' result map is keyed by - one entry
 *  per distinct (currency, targetCurrency, date) actually needed, not just
 *  per (currency, targetCurrency) - see dateKey's own doc comment for why a
 *  bare pairKey is no longer enough once bills convert at their own date. */
function dateKey(currency: string, targetCurrency: string, date: string | undefined): string {
  return `${pairKey(currency, targetCurrency)}@${date ?? ""}`;
}

/** One (currency, targetCurrency, date) key per DISTINCT date actually
 *  present for that currency's bills, PLUS one key for the advance's own
 *  date (the trip's departureAt, since AdvanceAmount itself carries no
 *  date - an advance is issued as one lump sum, not dated per line) when
 *  that currency has an advance line. Each bill converts at ITS OWN
 *  transaction date's rate (see buildFxLedger) rather than the whole
 *  currency's bills sharing one representative date's rate - three Food
 *  bills on three different days each get their own day's real rate,
 *  summed afterward, not one rate applied to all three. A bill with no
 *  date (an old one from before dates were mandatory) shares a `date:
 *  undefined` key with the advance's fallback below, both resolved to the
 *  latest rate by the backend. departureDate is optional only for a caller
 *  that genuinely has no trip date yet (shouldn't occur in practice - see
 *  CurrencyConversionCard's callers, which all have one). */
export function fxLedgerKeys(
  expenses: ExpenseLineItem[],
  advanceAmounts: AdvanceAmount[],
  fallbackCurrency: string,
  targetCurrencyByCurrency: Map<string, string>,
  departureDate?: string | null
): { currency: string; targetCurrency: string; date?: string }[] {
  const datesByCurrency = new Map<string, Set<string | undefined>>();
  const addDate = (cur: string, date: string | undefined) => {
    if (!datesByCurrency.has(cur)) datesByCurrency.set(cur, new Set());
    datesByCurrency.get(cur)!.add(date);
  };
  for (const e of expenses) {
    addDate(e.currency || fallbackCurrency, e.date || undefined);
  }
  const fxDepartureDate = toFxDate(departureDate);
  for (const a of advanceAmounts) {
    addDate(a.currency, fxDepartureDate);
  }
  const keys: { currency: string; targetCurrency: string; date?: string }[] = [];
  for (const [currency, dates] of datesByCurrency) {
    const targetCurrency = targetCurrencyByCurrency.get(currency) ?? fallbackCurrency;
    for (const date of dates) {
      keys.push({ currency, targetCurrency, date });
    }
  }
  return keys;
}

/** Fetches one rate per distinct (currency, targetCurrency, date) key
 *  actually needed (see fxLedgerKeys) - one request per bill DATE present
 *  in a currency, not one per currency. A pair whose currency already
 *  equals its own target is skipped (rate is always 1). Returned map
 *  contains ONLY the newly-fetched keys (merge it into whatever you
 *  already had - see CurrencyConversionCard's effect), keyed by
 *  dateKey(currency, targetCurrency, date), NOT the bare pairKey - a
 *  currency with bills on several dates needs several entries side by
 *  side, one per date.
 *
 *  `alreadyHave`, when given, is skipped entirely - changing ONE row's
 *  settlement currency used to re-request every OTHER row's rate too
 *  (fetchLedgerFxRates was always called with the full key set, and the
 *  caller's effect re-ran on any change to targetCurrencyByCurrency, which
 *  is a single Map covering every currency - confirmed live: picking a new
 *  target for one currency fired a GET /api/fx/rate for every row, not
 *  just the changed one). Rows whose (currency, targetCurrency, date) key
 *  is already present in alreadyHave are left untouched here; the caller
 *  merges this function's (now much smaller) result on top of its existing
 *  rates map instead of replacing it wholesale. */
export async function fetchLedgerFxRates(
  expenses: ExpenseLineItem[],
  advanceAmounts: AdvanceAmount[],
  fallbackCurrency: string,
  targetCurrencyByCurrency: Map<string, string>,
  departureDate?: string | null,
  alreadyHave?: Map<string, RateInfo>
): Promise<Map<string, RateInfo>> {
  const keys = fxLedgerKeys(expenses, advanceAmounts, fallbackCurrency, targetCurrencyByCurrency, departureDate).filter(
    (k) => k.currency !== k.targetCurrency && !alreadyHave?.has(dateKey(k.currency, k.targetCurrency, k.date))
  );
  const results = await Promise.all(
    keys.map(async (k) => {
      const rate = await fetchFxRate(k.currency, k.targetCurrency, k.date).catch(() => null);
      // Frankfurter resolves a weekend/holiday date to the nearest trading
      // day's rate on its own, so a dated bill always gets that exact day
      // asked for - usedFallbackRate only fires for a currency with no
      // dated bill at all (an advance-only currency, or an old bill from
      // before dates were mandatory).
      return [dateKey(k.currency, k.targetCurrency, k.date), { rate, asOfDate: k.date ?? null, usedFallbackRate: !k.date }] as const;
    })
  );
  return new Map(results);
}

/** Nets advance-vs-expense per currency, then converts each currency's
 *  leftover into ITS OWN target currency - the reconciliation ledger shown
 *  by CurrencyConversionCard. `targetCurrencyByCurrency` maps each present
 *  currency to the settlement currency it should convert into (defaulting
 *  to `defaultTargetCurrency` for one not listed); `rates` must have one
 *  entry per distinct (currency, target, date) key actually needed, keyed
 *  by dateKey (see fetchLedgerFxRates/fxLedgerKeys) - NOT a bare pairKey,
 *  since a currency with bills on several dates now needs a rate per date.
 *  `departureDate` is the advance's own conversion date (it has no date of
 *  its own - see fxLedgerKeys). `pendingOverrides` is a session-local,
 *  not-yet-saved rate per (currency, target) pair (takes priority over
 *  every fetched rate for that pair, collapsing the row back to one flat
 *  rate); `savedOverrides` is the persisted record per pair (takes priority
 *  over everything, including a pending override still being edited in the
 *  same session). Pure/sync so it recomputes instantly as a target currency
 *  or override changes, without a re-fetch. */
export function buildFxLedger(
  expenses: ExpenseLineItem[],
  advanceAmounts: AdvanceAmount[],
  fallbackCurrency: string,
  defaultTargetCurrency: string,
  targetCurrencyByCurrency: Map<string, string>,
  rates: Map<string, RateInfo>,
  pendingOverrides: Map<string, number> = new Map(),
  savedOverrides: FxOverride[] = [],
  departureDate?: string | null
): FxLedger {
  // Per currency: every bill summed INTO ITS OWN DATE group (not one
  // representative date for the whole currency), plus the advance as its
  // own "line" dated at departureDate - each line converts at its own
  // rate unless overridden (see the per-row loop below). billLinesByCurrency
  // mirrors this but WITHOUT the advance's own line - used to convert
  // actualExpense alone (Fixed Allowance's "Amount spent" tiles) at exactly
  // the same per-date rates, without a second rate fetch: every date it
  // needs is already a date linesByCurrency needs too (a strict subset).
  const linesByCurrency = new Map<string, Map<string | undefined, number>>();
  const billLinesByCurrency = new Map<string, Map<string | undefined, number>>();
  const addLine = (
    map: Map<string, Map<string | undefined, number>>,
    cur: string,
    date: string | undefined,
    amount: number
  ) => {
    if (!map.has(cur)) map.set(cur, new Map());
    const lines = map.get(cur)!;
    lines.set(date, (lines.get(date) || 0) + amount);
  };
  const spent = new Map<string, number>();
  for (const e of expenses) {
    const cur = e.currency || fallbackCurrency;
    spent.set(cur, (spent.get(cur) || 0) + e.amount);
    addLine(linesByCurrency, cur, e.date || undefined, e.amount);
    addLine(billLinesByCurrency, cur, e.date || undefined, e.amount);
  }
  const advanced = new Map(advanceAmounts.map((a) => [a.currency, a.amount]));
  // Same toFxDate truncation fxLedgerKeys applies to departureDate - this
  // key MUST match exactly what fetchLedgerFxRates actually fetched under
  // (dateKey includes the date string verbatim), or rates.get(...) below
  // misses even though a rate WAS fetched, just keyed by the untruncated
  // datetime instead of the plain date Frankfurter understands.
  const fxDepartureDate = toFxDate(departureDate);
  for (const a of advanceAmounts) {
    // Negative: the advance OFFSETS the bills (sameCurrOffset = spent -
    // advanced), so its own line must carry that same sign to sum
    // correctly alongside the bill lines above. Deliberately NOT added to
    // billLinesByCurrency - that map stays bills-only.
    addLine(linesByCurrency, a.currency, fxDepartureDate, -a.amount);
  }
  const currencies = new Set([...spent.keys(), ...advanced.keys()]);
  const savedByPair = new Map(savedOverrides.map((o) => [pairKey(o.currency, o.targetCurrency), o]));

  const netPaymentByCurrency = new Map<string, number>();
  const unconvertedByCurrency = new Map<string, number>();
  const spentByCurrency = new Map<string, number>();
  const rows: FxLedgerRow[] = [...currencies]
    .sort()
    .map((currency) => {
      const targetCurrency = targetCurrencyByCurrency.get(currency) ?? defaultTargetCurrency;
      const advanceIssued = advanced.get(currency) || 0;
      const actualExpense = spent.get(currency) || 0;
      const sameCurrOffset = actualExpense - advanceIssued;
      const key = pairKey(currency, targetCurrency);
      const saved = savedByPair.get(key) ?? null;
      const pending = pendingOverrides.get(key);

      const addNet = (amount: number) =>
        netPaymentByCurrency.set(targetCurrency, (netPaymentByCurrency.get(targetCurrency) || 0) + amount);
      const addUnconverted = () =>
        unconvertedByCurrency.set(targetCurrency, (unconvertedByCurrency.get(targetCurrency) || 0) + sameCurrOffset);
      const addSpent = (amount: number) =>
        spentByCurrency.set(targetCurrency, (spentByCurrency.get(targetCurrency) || 0) + amount);

      if (currency === targetCurrency) {
        addNet(sameCurrOffset);
        addSpent(actualExpense);
        return {
          currency, targetCurrency, advanceIssued, actualExpense, sameCurrOffset,
          rate: 1, multipleRates: false, rateBreakdown: [],
          savedOverride: null, isPendingOverride: false, asOfDate: null,
          valuationInTarget: sameCurrOffset, actualExpenseInTarget: actualExpense
        };
      }
      // An override collapses the WHOLE row back to one flat rate,
      // regardless of how many distinct bill dates are actually behind
      // sameCurrOffset - same behavior as before per-bill dating existed.
      if (saved) {
        const valuationInTarget = sameCurrOffset * saved.rate;
        const actualExpenseInTarget = actualExpense * saved.rate;
        addNet(valuationInTarget);
        addSpent(actualExpenseInTarget);
        return {
          currency, targetCurrency, advanceIssued, actualExpense, sameCurrOffset,
          rate: saved.rate, multipleRates: false, rateBreakdown: [],
          savedOverride: saved, isPendingOverride: false, asOfDate: null,
          valuationInTarget, actualExpenseInTarget
        };
      }
      if (pending !== undefined) {
        const valuationInTarget = sameCurrOffset * pending;
        const actualExpenseInTarget = actualExpense * pending;
        addNet(valuationInTarget);
        addSpent(actualExpenseInTarget);
        return {
          currency, targetCurrency, advanceIssued, actualExpense, sameCurrOffset,
          rate: pending, multipleRates: false, rateBreakdown: [],
          savedOverride: null, isPendingOverride: true, asOfDate: null,
          valuationInTarget, actualExpenseInTarget
        };
      }
      // No override: convert each (date, amount) line at ITS OWN rate and
      // sum. Any single line missing a rate makes the whole row
      // unconvertible - same "all or nothing per currency" contract as
      // before, just now checked per date instead of once. billBreakdown
      // mirrors this using billLinesByCurrency (bills only, no advance
      // line) at the SAME per-date rates, for actualExpenseInTarget - every
      // date it touches is also one `lines` touches, so no rate is ever
      // missing here that isn't already missing above.
      const lines = linesByCurrency.get(currency) ?? new Map<string | undefined, number>();
      const billLines = billLinesByCurrency.get(currency) ?? new Map<string | undefined, number>();
      const breakdown: FxRateUsed[] = [];
      let valuationInTarget = 0;
      let missingRate = false;
      for (const [date, amount] of lines) {
        const info = rates.get(dateKey(currency, targetCurrency, date));
        if (!info || info.rate === null) {
          missingRate = true;
          break;
        }
        breakdown.push({ date: date ?? null, rate: info.rate, amount });
        valuationInTarget += amount * info.rate;
      }
      if (missingRate || breakdown.length === 0) {
        addUnconverted();
        return {
          currency, targetCurrency, advanceIssued, actualExpense, sameCurrOffset,
          rate: null, multipleRates: false, rateBreakdown: [],
          savedOverride: null, isPendingOverride: false, asOfDate: null,
          valuationInTarget: null, actualExpenseInTarget: null
        };
      }
      let actualExpenseInTarget = 0;
      let billMissingRate = false;
      for (const [date, amount] of billLines) {
        const info = rates.get(dateKey(currency, targetCurrency, date));
        if (!info || info.rate === null) {
          billMissingRate = true;
          break;
        }
        actualExpenseInTarget += amount * info.rate;
      }
      if (!billMissingRate) addSpent(actualExpenseInTarget);
      addNet(valuationInTarget);
      const distinctRates = new Set(breakdown.map((b) => b.rate));
      const single = distinctRates.size === 1;
      return {
        currency, targetCurrency, advanceIssued, actualExpense, sameCurrOffset,
        actualExpenseInTarget: billMissingRate ? null : actualExpenseInTarget,
        rate: single ? breakdown[0].rate : null,
        multipleRates: !single,
        rateBreakdown: breakdown,
        savedOverride: null, isPendingOverride: false,
        asOfDate: single ? breakdown[0].date : null,
        valuationInTarget
      };
    })
    // A currency with nothing on either side (shouldn't occur - every row
    // comes from `currencies`, which only ever holds a key with at least one
    // side set - kept as a defensive filter, not load-bearing).
    .filter((row) => row.advanceIssued !== 0 || row.actualExpense !== 0);

  const toSortedArray = (m: Map<string, number>): FxNetPayment[] =>
    [...m.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, amount]) => ({ currency, amount }));

  return {
    defaultTargetCurrency,
    rows,
    netPayments: toSortedArray(netPaymentByCurrency),
    unconvertedOffsets: toSortedArray(unconvertedByCurrency),
    spentByTarget: toSortedArray(spentByCurrency)
  };
}
